import type { Faq } from 'libs/http/api/content/content.types';

import type { DeliveryTableRow } from 'ui/organisms/DeliveryTable/DeliveryTable.types';

type Translate = (key: string) => string;

export const getDeliveryRows = (t: Translate): DeliveryTableRow[] => [
  {
    name: t('Platform'),
    desc: t('Search a company in the browser and see its full group structure — no setup.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: t('Linkage API'),
    desc: t('One call with a company ID returns all 24 linkage fields as flat JSON.'),
    actions: [{ label: t('Read the docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('MCP server'),
    desc: t('Connect it and your own AI agents query group structures directly.'),
    actions: [{ label: t('Get the MCP server'), href: '/mcp-integration' }],
  },
  {
    name: t('Bulk files'),
    desc: t('Your customer or supplier file back, with the 24 linkage columns appended.'),
    actions: [{ label: t('Request a sample'), href: '/book-a-demo' }],
  },
  {
    name: t('Regis AI agent'),
    desc: t('Ask in plain language and get the resolved hierarchy, with the record behind it.'),
    actions: [{ label: t('Ask Regis'), href: '/regis' }],
  },
  {
    name: t('Claude & ChatGPT connectors'),
    desc: t('Install Global Database from each connector directory and ask across 600M+ companies.'),
    actions: [
      { label: 'Claude', href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: 'ChatGPT', href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: t('CRM integration'),
    desc: t(
      'Push the group hierarchy into your CRM — parent accounts, subsidiaries, and the group key on every record.',
    ),
    actions: [{ label: t('See how it works'), href: '/crm' }],
  },
];

export const getFaqData = (t: Translate): Faq[] => [
  {
    id: 'faq-1',
    default_opened: true,
    question: t('What is corporate linkage data?'),
    answer: t(
      'Corporate linkage data connects individual companies to the corporate group they belong to. For every company, it identifies the immediate parent, the domestic ultimate (the highest entity in the same country), and the global ultimate (the highest entity worldwide). Global Database delivers this as 24 flat fields — a persistent ID, legal name, full address, and ISO country code for each level — across 600M+ companies in 200+ countries.',
    ),
  },
  {
    id: 'faq-2',
    default_opened: true,
    question: t('What is a global ultimate parent company?'),
    answer: t(
      'The global ultimate parent is the company at the very top of a corporate group — the entity that is not owned or controlled by any other company. For example, NVIDIA Corporation is the global ultimate of NVIDIA Ltd (UK), Mellanox Technologies (Israel), and Nvidia Denmark ApS. It is the single anchor used to roll up group exposure, aggregate accounts, and screen counterparties at group level.',
    ),
  },
  {
    id: 'faq-3',
    default_opened: true,
    question: t('What is the difference between a domestic ultimate and a global ultimate?'),
    answer: t(
      'The domestic ultimate is the highest entity of a group within one country; the global ultimate is the highest entity worldwide. A UK subsidiary of a US group has a UK domestic ultimate (for in-country consolidation, tax grouping, and national exposure views) and a US global ultimate (for worldwide rollups). Global Database returns both on every record, each with its own ID, name, and full address.',
    ),
  },
  {
    id: 'faq-4',
    default_opened: true,
    question: t('How do I find the ultimate parent company of a business?'),
    answer: t(
      'Search the company on the Global Database platform and its full group structure is returned instantly — immediate parent, domestic ultimate, and global ultimate. For portfolios, send a file or call the Linkage API and every entity comes back with all 24 linkage fields appended, so you can resolve thousands of companies to their ultimate parents in one pass.',
    ),
  },
  {
    id: 'faq-5',
    default_opened: true,
    question: t('What fields are included in the corporate linkage dataset?'),
    answer: t(
      '24 fields in a flat schema: for each of the three levels (immediate parent, domestic ultimate, global ultimate) you receive a persistent Global Database unique ID, legal business name, street address, city, postal code, province or state, country, and ISO 3166-1 country code. The naming follows the parent / domestic ultimate / global ultimate conventions most data warehouses already use.',
    ),
  },
  {
    id: 'faq-6',
    default_opened: true,
    question: t('How many companies does the corporate linkage data cover?'),
    answer: t(
      "The linkage layer sits on Global Database's records of 600M+ companies across 200+ countries, so group structures can be resolved for entities in virtually any jurisdiction — including cross-border hierarchies where the parent, domestic ultimate, and global ultimate sit in three different countries.",
    ),
  },
  {
    id: 'faq-7',
    default_opened: true,
    question: t('What is the difference between corporate linkage and UBO (beneficial ownership) data?'),
    answer: t(
      'Corporate linkage answers “which group does this company belong to” — the entity-to-entity hierarchy up to the global ultimate. UBO data answers “who owns it” — shareholders and beneficial owners resolved to natural persons for compliance thresholds. Compliance teams typically use both: linkage for grouping, rollups, and account hierarchies; UBO for ownership and control analysis.',
    ),
  },
  {
    id: 'faq-8',
    default_opened: true,
    question: t('Can Global Database replace our existing corporate family tree feed?'),
    answer: t(
      'In most cases, yes. Because the schema uses the standard parent / domestic ultimate / global ultimate structure, mapping from an existing corporate family tree or linkage feed is a column-for-column exercise rather than a re-architecture. The recommended path is a side-by-side match test on your own portfolio before switching anything off.',
    ),
  },
  {
    id: 'faq-9',
    default_opened: true,
    question: t('How is the corporate linkage data delivered?'),
    answer: t(
      'Four ways: the Global Database platform for instant lookups and exports; the Linkage API returning all 24 fields as flat JSON with batch endpoints for portfolio-scale resolution; bulk file delivery for your data warehouse with delta files to keep structures current; and Regis, the AI agent, for plain-language questions like “who ultimately owns this supplier?”.',
    ),
  },
  {
    id: 'faq-10',
    default_opened: true,
    question: t('Can we test the corporate linkage data before buying?'),
    answer: t(
      'Yes. The standard evaluation is a match test: send a sample of your customer, supplier, or portfolio entities and we return the file with all 24 linkage fields appended, together with the full data dictionary. Your team verifies match rates and hierarchy accuracy against accounts you already know before committing.',
    ),
  },
];
